import * as React from "react";
import { createPortal } from "react-dom";
import { IconCoin, IconDiamond, IconCards, IconSparkles } from "../../components/icons.jsx";
import { PenaltyScene } from "./PenaltyScene.jsx";

const steps = [
  { title: "Выберите валюту и ставку", text: "Играйте на MetaCoin, кристаллы, крутки или рубины. Укажите сумму и начните раунд." },
  { title: "Выберите направление удара", text: "Бейте влево или вправо — шансы одинаковы." },
  { title: "Забивайте и увеличивайте выигрыш", text: "С каждым голом множитель растёт. На ×20 выигрыш выплачивается автоматически." },
  { title: "Заберите выигрыш", text: "Остановитесь после любого гола или продолжайте игру. Выплата включает ставку; дробная часть отбрасывается." },
  { title: "Остерегайтесь сейва", text: "Если вратарь поймает мяч, раунд завершится: ставка и накопленный выигрыш потеряются." },
];
const formatMultiplier = value => `×${(value / 100).toLocaleString("ru-RU", { maximumFractionDigits: 2 })}`;
const SLIDE_DURATION_MS = 5_000;

function Illustration({ step, rules }) {
  const stake = rules?.currencies.metacoin.minimum || 10;
  const multipliers = rules?.multipliers || [110, 133, 203, 320, 483];
  if (step === 0) return <div className="penalty-guide-bet" aria-hidden="true">
    <div className="penalty-guide-wallets">{[IconCoin, IconDiamond, IconCards, IconSparkles].map((Icon, index) => <span key={index} className={index === 0 ? "selected" : ""}><Icon /></span>)}</div>
    <div className="penalty-guide-start">Начать раунд</div>
    <div className="penalty-guide-amount"><span>−</span><b><IconCoin />{stake}</b><span>+</span></div>
  </div>;
  return <div className={`penalty-guide-field penalty-guide-step-${step}`} aria-hidden="true">
    {(step === 2 || step === 4) && <div className="penalty-guide-scale">{multipliers.slice(0, 5).map((value, index) => <span className={index === 2 ? "selected" : ""} key={value}>{formatMultiplier(value)}</span>)}</div>}
    <PenaltyScene round={step === 4 ? { status: "lost" } : null} />
    {(step === 1 || step === 3) && <div className="penalty-guide-directions"><span>◀ Влево</span><span>Вправо ▶</span></div>}
    {step === 3 && <div className="penalty-guide-claim">Забрать <IconCoin />{Math.floor(stake * multipliers[0] / 100)}</div>}
    {step === 2 && <strong className="penalty-guide-result">ГОЛ!</strong>}
    {step === 4 && <strong className="penalty-guide-result">СЕЙВ!</strong>}
  </div>;
}

export function PenaltyRules({ rules, onClose }) {
  const [step, setStep] = React.useState(0);
  const [restart, setRestart] = React.useState(0);
  const close = React.useRef(onClose);
  close.current = onClose;
  const dialog = React.useRef(null);
  const touch = React.useRef(null);
  React.useEffect(() => {
    const element = dialog.current;
    const previousFocus = document.activeElement;
    element.showModal();
    return () => {
      element.close();
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, []);
  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      if (step === steps.length - 1) close.current();
      else setStep(step + 1);
    }, SLIDE_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [step, restart]);
  function selectStep(index) {
    setStep(index);
    setRestart(current => current + 1);
  }
  function swipe(event) {
    const start = touch.current;
    touch.current = null;
    const end = event.changedTouches[0];
    if (!start || !end) return;
    const delta = end.clientX - start.x;
    if (Math.abs(delta) > 40 && Math.abs(delta) > Math.abs(end.clientY - start.y)) {
      selectStep(Math.max(0, Math.min(steps.length - 1, step + (delta < 0 ? 1 : -1))));
    }
  }
  return createPortal(<dialog ref={dialog} className="penalty-guide" aria-labelledby="penalty-guide-title" aria-describedby="penalty-guide-text"
    onCancel={event => { event.preventDefault(); onClose(); }}
    onClick={event => {
      if (event.target !== event.currentTarget) return;
      const rect = event.currentTarget.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) onClose();
    }}>
    <button type="button" className="penalty-guide-close" aria-label="Закрыть правила" onClick={onClose}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg></button>
    <div className="penalty-guide-art" onTouchStart={event => { const point = event.touches[0]; touch.current = { x: point.clientX, y: point.clientY }; }} onTouchEnd={swipe} onTouchCancel={() => { touch.current = null; }}>
      <Illustration step={step} rules={rules} />
    </div>
    <div className="penalty-guide-copy" aria-live="polite"><h2 id="penalty-guide-title">{steps[step].title}</h2><p id="penalty-guide-text">{steps[step].text}</p></div>
    <nav className="penalty-guide-dots" aria-label="Шаги обучения" style={{ "--guide-duration": `${SLIDE_DURATION_MS}ms` }}>{steps.map((entry, index) => <button type="button" key={entry.title} aria-label={`Шаг ${index + 1}: ${entry.title}`} aria-current={step === index ? "step" : undefined} onClick={() => selectStep(index)}><span key={step === index ? `${step}:${restart}` : "idle"} /></button>)}</nav>
    <button type="button" className={`penalty-guide-next ${step === steps.length - 1 ? "is-last" : ""}`} onClick={() => step === steps.length - 1 ? onClose() : selectStep(step + 1)}>{step === steps.length - 1 ? "Понятно" : "Продолжить"}</button>
  </dialog>, document.body);
}
